import { normalizarTexto } from '../../lib/textoBR'
import CampoNomeDeTabela from './CampoNomeDeTabela'
import type { Agrupamento } from '../../importacao/condicoes'

interface Props {
  agrupamento: Agrupamento
  colunas: string[]
  linhas: string[][]
  colunasDiferentes: number[]
  jaImportadas: Array<{ numero: number; tabelaNome: string }>
  escolhida: number | null
  tabelaNome: string
  pedirNomeTabela: boolean
  // `undefined` = consulta ainda respondendo. Ver CampoNomeDeTabela.
  tabelasExistentes: string[] | undefined
  aoEscolher: (numero: number) => void
  aoMudarTabela: (nome: string) => void
  aoVoltar: () => void
  aoConfirmar: () => void
}

const MAX_COLUNAS = 4
const MAX_EXEMPLOS = 2
const MAX_UNICOS = 5

export default function EscolhaCondicao({
  agrupamento, colunas, linhas, colunasDiferentes, jaImportadas, escolhida,
  tabelaNome, pedirNomeTabela, tabelasExistentes, aoEscolher, aoMudarTabela,
  aoVoltar, aoConfirmar,
}: Props) {
  const { condicoes, unicos, repetidos } = agrupamento
  const mostradas = colunasDiferentes.slice(0, MAX_COLUNAS)
  const exemplos = repetidos.slice(0, MAX_EXEMPLOS)
  const restantes = condicoes.filter((c) => !jaImportadas.some((j) => j.numero === c.numero))

  const conflito =
    pedirNomeTabela &&
    tabelaNome.trim() !== '' &&
    jaImportadas.find((j) => normalizarTexto(j.tabelaNome) === normalizarTexto(tabelaNome))

  const podeContinuar =
    escolhida !== null && !conflito && (!pedirNomeTabela || tabelaNome.trim() !== '')

  // valor cru de uma coluna, na linha daquele codigo dentro daquela condicao
  function valor(numero: number, codigo: string, coluna: number): string {
    const linha = condicoes.find((c) => c.numero === numero)?.linhas
      .find((l) => l.campos.codigo === codigo)
    if (!linha) return '—'
    return (linhas[linha.indice - 1]?.[coluna] ?? '').trim() || '—'
  }

  function descricao(codigo: string): string {
    for (const condicao of condicoes) {
      const linha = condicao.linhas.find((l) => l.campos.codigo === codigo)
      if (linha) return linha.campos.descricao
    }
    return codigo
  }

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
        <h3 className="text-base font-bold text-slate-800">
          Este arquivo tem {condicoes.length} condições do mesmo produto
        </h3>
        <p className="text-sm text-slate-600">
          {repetidos.length} produtos aparecem mais de uma vez. O que muda entre as condições:
        </p>
      </div>

      {condicoes.length >= 3 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
          <p className="text-sm text-amber-800">
            ⚠ Este arquivo tem {condicoes.length} condições do mesmo produto — é fora do comum.
            Confira os exemplos abaixo. Se as linhas não parecem o mesmo produto repetido,
            volte ao mapeamento: a coluna de código pode estar errada.
          </p>
          <button type="button" onClick={aoVoltar} className="text-sm text-teal-700 underline">
            ← Voltar ao mapeamento
          </button>
        </div>
      )}

      <div className="space-y-2">
        {restantes.map((condicao) => (
          <label
            key={condicao.numero}
            className={`block bg-white rounded-xl shadow-sm p-4 cursor-pointer border-2 ${
              escolhida === condicao.numero ? 'border-teal-600' : 'border-transparent'
            }`}
          >
            <span className="flex items-center gap-2">
              <input
                type="radio" name="condicao" checked={escolhida === condicao.numero}
                onChange={() => aoEscolher(condicao.numero)}
                aria-label={`Condição ${condicao.numero}`}
              />
              <span className="text-sm font-medium text-slate-800">
                Condição {condicao.numero} — {condicao.linhas.length} produtos
              </span>
            </span>
            <span className="block mt-2 overflow-x-auto">
              <table className="text-xs w-full">
                <thead>
                  <tr>
                    <th className="text-left px-2 py-1 text-slate-500">produto</th>
                    {mostradas.map((coluna) => (
                      <th key={coluna} className="text-left px-2 py-1 text-slate-500">
                        {colunas[coluna]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {exemplos.map((codigo) => (
                    <tr key={codigo} className="border-t border-slate-100">
                      {/* codigo + descricao como ancora, no mesmo formato do
                          passo de comissoes ("CE-3 — Produto CE-3") */}
                      <td className="px-2 py-1 text-slate-700">{codigo} — {descricao(codigo)}</td>
                      {mostradas.map((coluna) => (
                        <td key={coluna} className="px-2 py-1 text-slate-700">
                          {valor(condicao.numero, codigo, coluna)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {colunasDiferentes.length > MAX_COLUNAS && (
                <span className="block text-xs text-slate-400 px-2 py-1">
                  … e mais {colunasDiferentes.length - MAX_COLUNAS} colunas diferentes.
                </span>
              )}
            </span>
          </label>
        ))}
      </div>

      {unicos.length > 0 && (
        <p className="text-sm text-slate-600 bg-slate-100 rounded-lg p-3">
          {unicos.length} produto(s) aparecem uma vez só no arquivo (
          {unicos.slice(0, MAX_UNICOS).map((u) => u.descricao).join(', ')}
          {unicos.length > MAX_UNICOS ? `, e mais ${unicos.length - MAX_UNICOS}` : ''}
          ) — entram em todas as condições, com o mesmo preço.
        </p>
      )}

      {pedirNomeTabela ? (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <CampoNomeDeTabela
            id="tabela-condicao"
            rotulo="Nome da tabela de preço desta condição"
            valor={tabelaNome}
            aoMudar={aoMudarTabela}
            existentes={tabelasExistentes}
          />
        </div>
      ) : (
        <p className="text-sm text-slate-600">
          Escolha qual condição vai para a tabela «{tabelaNome.trim()}».
        </p>
      )}

      {conflito && (
        <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
          A tabela «{tabelaNome.trim()}» já recebeu a condição {conflito.numero} deste arquivo.
          Importar outra condição nela trocaria os preços que você acabou de importar.
          Dê outro nome para a tabela desta condição.
        </p>
      )}

      <button
        onClick={aoConfirmar}
        disabled={!podeContinuar}
        className="w-full bg-teal-700 text-white rounded-lg py-2 font-medium disabled:opacity-50"
      >
        Continuar
      </button>
      {condicoes.length < 3 && (
        <button type="button" onClick={aoVoltar} className="w-full text-slate-600 text-sm underline py-2">
          ← Voltar ao mapeamento
        </button>
      )}
    </div>
  )
}
